import React, {useCallback} from 'react';
import {
  TList,
  TRecord,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  withSystem,
  withMeta,
  Plugins,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {FORM_DEFAULT_LABEL, FORM_OPTS_PATH, ATTR_ID_PATH} from '../constants';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {useUpdateSiblingLabel} from './formHooks';
import {usysFieldType, USYS_FIELD_PATH} from '@packages/systems/users/usysWfdl';
import {UserFormRequired} from '../UserFormRequired';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';

const {getIn} = ContextualExpressions;

const Grid = emotionStyled('div')({
  display: 'grid',
  gap: tokens.space[2],
});

const FIRST_OPT_PATH: ExpressionPath = [
  ...FORM_OPTS_PATH,
  {in: 'List', index: 0},
  {in: 'Record', at: 't'},
];

const type = {
  users: usysFieldType,
  form: withSystem(
    {labelVariant: 'hidden'},
    TRecord({
      name: withSystem(
        {label: 'Name', defaultValue: FORM_DEFAULT_LABEL, isLocalizable: true},
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
      ),
      opts: withMeta(
        {sortable: true},
        withSystem(
          {private: true, label: 'Choices', isLocalizable: true},
          TList(
            TTypeConstructor(
              qualify(Plugins.name('Form'), typeName('FormSelectOption'))
            )
          )
        )
      ),
      type: withSystem({defaultValue: 'select', private: true}, TText),
    })
  ),
  attr: TRecord({
    id: withSystem(
      {defaultValue: FORM_DEFAULT_LABEL, isLocalizable: true},
      TText
    ),
    name: withSystem(
      {private: true, defaultValue: FORM_DEFAULT_LABEL, isLocalizable: true},
      TText
    ),
    'data-name': withSystem(
      {defaultValue: FORM_DEFAULT_LABEL, private: true, isLocalizable: true},
      TText
    ),
    required: withSystem(
      {
        private: true,
        defaultValue: false,
        label: 'Required',
        labelVariant: 'hidden',
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
  }),
} as const;

// @ts-expect-error - TS7006 - Parameter 'path' implicitly has an 'any' type.
const getExpressionFromPath = (expr: ContextualExpression, path) => {
  const expression = getIn(expr, path);
  if (!expression)
    throw Error(`failed to get expression at path: ${JSON.stringify(path)}`);
  return expression;
};

const getExpressionText = (
  expr: ContextualExpression,
  path: ExpressionPath
) => {
  return (
    ContextualExpressions.getText(
      stripAnn(getExpressionFromPath(expr, path))
    ) ?? ''
  );
};

const UserFormSelectType = createDevLinkRootAlias({
  fields: type,
  params: ['extraAtomFields'],
  render: function FormSelectRender({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: any;
  }) {
    const id =
      ContextualExpressions.getText(
        stripAnn(getExpressionFromPath(expr, ATTR_ID_PATH))
      ) || '';
    useUpdateSiblingLabel(id);
    const dispatchAt = extendExpressionEditorActionAtPath(dispatch);

    const firstOptionExpr = getExpressionFromPath(expr, FIRST_OPT_PATH);
    const firstOptionDispatch = useCallback(dispatchAt(FIRST_OPT_PATH), [
      dispatchAt,
    ]);
    const userFieldId = getExpressionText(expr, USYS_FIELD_PATH);

    return (
      <Grid>
        {/* First Option Text */}
        <ExpressionEditor
          expression={firstOptionExpr}
          dispatch={firstOptionDispatch}
        />
        {/* Required (read only) */}
        <UserFormRequired fieldId={userFieldId} />
      </Grid>
    );
  },
});

export default UserFormSelectType;
